import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link, useNavigate, useParams } from "react-router-dom";
import { PASSWORD_MIN, checkPassword } from "@brasilnovo/shared";
import { api } from "../lib/api.js";
import { useAuth } from "../lib/auth.js";
import { ErrorBox, Spinner } from "../components/ui.js";

/**
 * Convite (D-27): a pessoa recebe um link PRIVADO de quem administra o
 * portal e define aqui a própria senha. O link vale uma vez e por 7 dias.
 * Pública de propósito — quem abre ainda não tem login.
 */
export function Convite() {
  const { token = "" } = useParams();
  const { acceptInvite } = useAuth();
  const navigate = useNavigate();
  const [nova, setNova] = useState("");
  const [confirma, setConfirma] = useState("");
  const [erro, setErro] = useState<unknown>(null);
  const [salvando, setSalvando] = useState(false);

  const info = useQuery({
    queryKey: ["convite", token],
    retry: false,
    queryFn: async () => (await api.get(`/auth/invite/${encodeURIComponent(token)}`)).data as { name: string; email: string },
  });

  const problemas = nova ? checkPassword(nova).problemas : [];
  const podeSalvar = nova.length > 0 && problemas.length === 0 && nova === confirma && !salvando;

  async function submeter(e: React.FormEvent) {
    e.preventDefault();
    setErro(null);
    setSalvando(true);
    try {
      await acceptInvite(token, nova);
      navigate("/", { replace: true });
    } catch (err) {
      setErro(err);
    } finally {
      setSalvando(false);
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-brand-50 px-4 py-10">
      <div className="w-full max-w-md overflow-hidden rounded-2xl bg-white shadow-sm ring-1 ring-brand-100">
        <div className="brand-stripe h-1.5" />
        <div className="px-7 py-8">
          <div className="mb-6 rounded-xl bg-brand-600 px-3 py-3">
            <img src="/logo-grupo-brasil-novo.png" alt="Grupo Brasil Novo" width={347} height={94} className="mx-auto h-auto w-56" />
          </div>
          {info.isPending ? (
            <Spinner label="Conferindo o convite…" />
          ) : info.isError ? (
            <div data-testid="convite-invalido" className="text-center">
              <h1 className="text-xl font-semibold text-brand-950">Convite expirado ou já usado</h1>
              <p className="mt-2 text-sm leading-relaxed text-slate-600">
                Cada link vale uma vez e por 7 dias. Peça um novo a quem administra o portal
                ou, se já definiu sua senha, entre normalmente.
              </p>
              <Link to="/login" className="btn-primary mt-5 inline-flex">Ir para o login</Link>
            </div>
          ) : (
            <form onSubmit={submeter} data-testid="convite-form">
              <h1 className="text-2xl font-semibold text-brand-950">Bem-vindo(a), {info.data.name.split(" ")[0]}</h1>
              <p className="mt-1 text-sm text-slate-600">
                Defina sua senha de acesso ao Controle Operacional. Ela é só sua: ninguém mais a vê.
              </p>
              <label className="label mt-5">E-mail de acesso</label>
              <input className="input mb-4 bg-slate-50" value={info.data.email} readOnly autoComplete="username" />

              <label className="label">Nova senha</label>
              <input className="input" type="password" autoComplete="new-password" data-testid="convite-senha"
                value={nova} onChange={(e) => setNova(e.target.value)} required />
              <ul className="mb-4 mt-1 space-y-0.5 text-xs">
                {problemas.length > 0
                  ? problemas.map((p) => <li key={p} className="text-amber-700">• {p}</li>)
                  : <li className="text-slate-400">{nova ? "Senha aceita." : `Ao menos ${PASSWORD_MIN} caracteres, combinando 3 tipos (maiúscula, minúscula, número, símbolo).`}</li>}
              </ul>

              <label className="label">Confirme a senha</label>
              <input className="input" type="password" autoComplete="new-password" data-testid="convite-confirma"
                value={confirma} onChange={(e) => setConfirma(e.target.value)} required />
              {confirma && nova !== confirma
                ? <p className="mb-4 mt-1 text-xs text-red-600">As duas senhas não conferem.</p>
                : <div className="mb-4" />}

              {erro != null && <div className="mb-4"><ErrorBox error={erro} /></div>}
              <button type="submit" className="btn-primary w-full justify-center" disabled={!podeSalvar}>
                {salvando ? "Salvando…" : "Definir senha e entrar"}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
